/*
 * benvel-lookbook-detail.js — <look-add-to-cart> custom element
 * Story 16.2 (Epic 16 — Lookbook detail + Shop The Look bundle ATC)
 *
 * Cart-write contract (audit AddLookToCartCTA + D1 + Story 14.4 precedent):
 *   - POST /cart/add.js with { items: [{ id, quantity, properties }] }
 *   - properties._benvel_look_id = look slug (CartDrawer reads this to
 *     branch into the RecommendedLookBlock state per audit components.md L80)
 *   - 200 success: fire benvel:cart-updated (source: 'look-detail-page',
 *     lookId, items). The CartDrawer subscribes and opens with the look
 *     branch.
 *   - 422 sold-out: HOLD the cart write (no partial-add). Mark affected
 *     bundle cards with inline "Sin stock — quitar para continuar" + shift
 *     focus to the first offending card.
 *
 * Consent-gated analytics events (Habeas Data + Story 11.4a):
 *   - benvel:look-detail-viewed       (page load)
 *   - benvel:look-bundle-cart-added   (200 success)
 *   - benvel:look-bundle-add-failed   (422 sold-out)
 *   - benvel:look-piece-swap-opened   (swap-piece card click)
 *   All gated on window.benvel.consent.has('analytics').
 */

(function () {
  "use strict";

  const ANALYTICS_KEY = "analytics";

  function consentGranted() {
    try {
      return Boolean(
        window.benvel &&
          window.benvel.consent &&
          typeof window.benvel.consent.has === "function" &&
          window.benvel.consent.has(ANALYTICS_KEY),
      );
    } catch (_err) {
      return false;
    }
  }

  function emitConsentGated(name, detail) {
    if (!consentGranted()) return;
    document.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail }),
    );
  }

  function parseVariantIds(raw) {
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  class LookAddToCart extends HTMLElement {
    constructor() {
      super();
      this._submitting = false;
    }

    connectedCallback() {
      this.button = this.querySelector('[data-bv-action="add-look-to-cart"]');
      this.live = this.querySelector("[data-bv-cart-live]");
      this.lookSlug = this.dataset.lookSlug || "";
      this.lookTitle = this.dataset.lookTitle || "";
      this.variantIds = parseVariantIds(this.dataset.lookVariantIds);
      this.pieceCount = Number(this.dataset.lookPieceCount) || this.variantIds.length;

      if (!this.button || this.variantIds.length === 0) {
        return;
      }

      this._onClick = this._onClick.bind(this);
      this.button.addEventListener("click", this._onClick);

      // Fire page-view analytics event once.
      emitConsentGated("benvel:look-detail-viewed", {
        lookId: this.lookSlug,
        slug: this.lookSlug,
        pieceCount: this.pieceCount,
      });
    }

    disconnectedCallback() {
      if (this.button && this._onClick) {
        this.button.removeEventListener("click", this._onClick);
      }
    }

    async _onClick(event) {
      event.preventDefault();
      if (this._submitting) return;
      this._submitting = true;
      this._setBusy(true);
      this._clearInlineStatuses();

      const body = {
        items: this.variantIds.map((id) => ({
          id,
          quantity: 1,
          properties: { _benvel_look_id: this.lookSlug },
        })),
      };

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          this._announce(""); // clear
          this._dispatchCartUpdated(data);
          emitConsentGated("benvel:look-bundle-cart-added", {
            lookId: this.lookSlug,
            items: this.variantIds,
            total: (data && data.total) || null,
          });
        } else if (response.status === 422) {
          const errorBody = await response.json().catch(() => ({}));
          this._handle422(errorBody);
        } else {
          this._announce("No se pudo añadir el look. Intenta de nuevo.");
        }
      } catch (_err) {
        this._announce("Error de red. Verifica tu conexión.");
      } finally {
        this._submitting = false;
        this._setBusy(false);
      }
    }

    _setBusy(busy) {
      if (!this.button) return;
      this.button.setAttribute("aria-busy", busy ? "true" : "false");
      if (busy) {
        this.button.setAttribute("aria-disabled", "true");
      } else {
        this.button.removeAttribute("aria-disabled");
      }
    }

    _announce(message) {
      if (this.live) {
        this.live.textContent = message || "";
      }
    }

    _dispatchCartUpdated(cartJson) {
      document.dispatchEvent(
        new CustomEvent("benvel:cart-updated", {
          bubbles: true,
          detail: {
            source: "look-detail-page",
            lookId: this.lookSlug,
            items: this.variantIds,
            cart: cartJson || null,
          },
        }),
      );
    }

    _clearInlineStatuses() {
      const cards = document.querySelectorAll(
        ".bv-look-bundle-card[data-piece-variant-id]",
      );
      cards.forEach((card) => {
        card.removeAttribute("data-bv-sold-out");
        const slot = card.querySelector("[data-bv-sold-out-slot]");
        if (slot) slot.textContent = "";
      });
    }

    _handle422(errorBody) {
      // Shopify returns { status: 422, description: "...", message: "..." }
      // Description can mention specific variant IDs that are unavailable.
      const description =
        (errorBody && (errorBody.description || errorBody.message)) || "";
      this._announce(
        "Algunas piezas no están disponibles. Quita las que están agotadas y vuelve a intentar.",
      );

      // Identify affected variants. Shopify's 422 description usually
      // contains the variant title rather than ID for bundle adds. As a
      // conservative default, mark EVERY available card with the inline
      // status if we can't pinpoint; if the description includes a
      // numeric ID, only mark that one.
      const cards = Array.from(
        document.querySelectorAll(
          ".bv-look-bundle-card[data-piece-variant-id]",
        ),
      );
      let firstOffender = null;
      const numericMatch = description.match(/\b\d{10,}\b/g);
      const numericMatches = numericMatch
        ? numericMatch.map((s) => Number(s))
        : [];

      cards.forEach((card) => {
        const variantId = Number(card.dataset.pieceVariantId);
        const isSoldOut =
          (numericMatches.length > 0 && numericMatches.includes(variantId)) ||
          card.dataset.pieceAvailable === "false";
        if (isSoldOut || numericMatches.length === 0) {
          card.setAttribute("data-bv-sold-out", "true");
          const slot = card.querySelector("[data-bv-sold-out-slot]");
          if (slot) {
            slot.textContent = "Sin stock — quitar para continuar";
          }
          if (!firstOffender) firstOffender = card;
        }
      });

      if (firstOffender) {
        try {
          firstOffender.focus({ preventScroll: false });
        } catch (_err) {
          /* noop */
        }
      }

      emitConsentGated("benvel:look-bundle-add-failed", {
        lookId: this.lookSlug,
        failedVariantIds: numericMatches,
      });
    }
  }

  if (!customElements.get("look-add-to-cart")) {
    customElements.define("look-add-to-cart", LookAddToCart);
  }

  /* ----------------------------------------------------------------------
   * Swap-piece card click — emit consent-gated analytics event.
   * The static alternatives v1 navigates to the alternative product's PDP
   * (default href behavior preserved). v2 (Story 18.6) intercepts the click
   * to mount AIStylistSwapPanel — we leave the default href intact here.
   * ---------------------------------------------------------------------- */
  document.addEventListener("click", (event) => {
    const card = event.target.closest("[data-bv-swap-target]");
    if (!card) return;
    const lookRoot = document.querySelector(
      "[data-bv-surface='look-detail']",
    );
    const lookSlug =
      (lookRoot && lookRoot.getAttribute("data-look-slug")) || null;
    emitConsentGated("benvel:look-piece-swap-opened", {
      lookId: lookSlug,
      productId: card.getAttribute("data-alt-product-id"),
      productHandle: card.getAttribute("data-alt-product-handle"),
    });
  });
})();
